import { AllEvents, Block, DataLayerEventName } from '@news-mono/web-common'
import React from 'react'

/** How long the end of the article must remain viewable before the event fires
 * so we don't capture false positives when the user quickly scrolls past the end of the article.
 */
export const ARTICLE_END_DWELL_MS = 1000

export interface ArticleEndMarkerProps {
    onEvent: (event: AllEvents) => void
    publicationPathname?: string
    wordCount: number
    dwellMs?: number
}

/**
 * Invisible marker placed directly after the last content block of an article.
 * Raises `article.read_complete` once, when the marker has been within the viewport
 * for `dwellMs`.
 */
export const ArticleEndMarker: React.FC<ArticleEndMarkerProps> = ({
    onEvent,
    publicationPathname,
    wordCount,
    dwellMs = ARTICLE_END_DWELL_MS,
}) => {
    const markerRef = React.useRef<HTMLDivElement | null>(null)
    const eventArgsRef = React.useRef({
        onEvent,
        publicationPathname,
        wordCount,
    })
    eventArgsRef.current = { onEvent, publicationPathname, wordCount }

    React.useEffect(() => {
        const marker = markerRef.current
        if (!marker || typeof IntersectionObserver === 'undefined') {
            return
        }

        const mountedAt = Date.now()
        let isFirstObservation = true
        let visibleWithoutScroll = false
        let dwellTimer: ReturnType<typeof setTimeout> | undefined

        const observer = new IntersectionObserver(([entry]) => {
            if (isFirstObservation) {
                isFirstObservation = false
                visibleWithoutScroll = entry.isIntersecting
            }

            if (!entry.isIntersecting) {
                clearTimeout(dwellTimer)
                dwellTimer = undefined
                return
            }

            if (dwellTimer !== undefined) {
                return
            }

            dwellTimer = setTimeout(() => {
                observer.disconnect()
                const { onEvent, publicationPathname, wordCount } =
                    eventArgsRef.current
                onEvent({
                    type: DataLayerEventName.articleReadComplete,
                    originator: 'ArticleEndMarker',
                    payload: {
                        publication_path: publicationPathname,
                        word_count: wordCount,
                        time_on_article_ms: Date.now() - mountedAt,
                        visible_without_scroll: visibleWithoutScroll,
                    },
                })
            }, dwellMs)
        })

        observer.observe(marker)

        return () => {
            clearTimeout(dwellTimer)
            observer.disconnect()
        }
    }, [dwellMs])

    return (
        <div
            ref={markerRef}
            aria-hidden
            data-article-end
            style={{ height: 1, marginTop: -1 }}
        />
    )
}
ArticleEndMarker.displayName = 'ArticleEndMarker'

const countWords = (text: unknown) =>
    typeof text === 'string' ? text.split(/\s+/).filter(Boolean).length : 0

/** Approximate word count of the text content in the given blocks */
export const countWordsInBlocks = (blocks: Block[]): number =>
    blocks.reduce((total, block) => {
        switch (block.kind) {
            case 'text':
            case 'subhead':
                return total + countWords(block.text)
            case 'ordered-list':
            case 'unordered-list':
                return (
                    total +
                    (Array.isArray(block.items) ? block.items : []).reduce(
                        (listTotal, item) =>
                            item?.kind === 'list-item' &&
                            Array.isArray(item.blocks)
                                ? listTotal + countWordsInBlocks(item.blocks)
                                : listTotal,
                        0,
                    )
                )
            default:
                return total
        }
    }, 0)
